import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BasketServices from "../../components/basket/basketService/BasketService";
import { IProductDetail } from "../../components/product-detail-component/type/Type";

export const fetchBasketData = createAsyncThunk(
  "basket/fetchBasketData",
  async (_, { rejectWithValue }) => {
    try {
      const res = await BasketServices.AllBasket();
      return res.data;
    } catch (err) {
      return rejectWithValue("Failed to fetch basket data");
    }
  }
);

interface BasketItem {
  id: number;
  product: IProductDetail;
  product_id: number;
  quantity: number;
  discount_price: number;
  price: number;
  cover_image: string;
  name: string;
}

interface BasketState {
  basketItems: BasketItem[];
  totalQuantity: number;
  totalPrice: number;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: BasketState = {
  basketItems: [],
  totalQuantity: 0,
  totalPrice: 0,
  status: "idle",
  error: null,
};

const basketSlice = createSlice({
  name: "basket",
  initialState,
  reducers: {
    clearBasket(state) {
      state.basketItems = [];
      state.totalQuantity = 0;
      state.totalPrice = 0;
    },
    addItem(state, action) {
      const newItem = action.payload;
      const existingItem = state.basketItems.find(
        (item) => item.id === newItem.id
      );
      if (existingItem) {
        existingItem.quantity += newItem.quantity;
      } else {
        state.basketItems.push(newItem);
      }
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );

      state.totalPrice = state.basketItems.reduce(
        (total, item) =>
          total + (item.discount_price || item.price) * item.quantity,
        0
      );
    },

    incrementQuantity(state, action) {
      const item = state.basketItems.find((item) => item.id === action.payload);
      if (item) {
        item.quantity += 1;
      }
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
    },

    decrementQuantity(state, action) {
      const item = state.basketItems.find((item) => item.id === action.payload);
      if (item && item.quantity > 1) {
        item.quantity -= 1;
      }
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
    },

    removeItem(state, action) {
      state.basketItems = state.basketItems.filter(
        (item) => item.product_id !== action.payload
      );
      state.totalQuantity = state.basketItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchBasketData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchBasketData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.basketItems = action.payload;

        state.totalQuantity = action.payload.reduce(
          (total: number, item: BasketItem) => total + item.quantity,
          0
        );

        state.totalPrice = action.payload.reduce(
          (total: number, item: BasketItem) => {
            return total + (item.discount_price || item.price) * item.quantity;
          },
          0
        );
      })
      .addCase(fetchBasketData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  clearBasket,
  addItem,
  incrementQuantity,
  decrementQuantity,
  removeItem,
} = basketSlice.actions;
export default basketSlice.reducer;
